<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  channel: {
    type: Object,
    default: () => ({}),
  },
  pinnedMessages: {
    type: Array,
    default: () => [],
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'back',
  'info',
  'scrollToPinned',
  'toggleFavorite',
  'toggleMute',
  'closeDm',
  'leave',
]);

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const showMenu = ref(false);

const isDM = computed(() => props.channel.channel_type === 'dm');
const isPrivate = computed(() => props.channel.channel_type === 'private_channel');
const isAdmin = computed(() => {
  const membership = (props.channel.members || []).find(
    m => m.user_id === currentUser.value?.id
  );
  return membership?.role === 'admin';
});

const dmPeer = computed(() => {
  if (!isDM.value) return null;
  const members = props.channel.members || [];
  return members.find(m => m.user_id !== currentUser.value?.id) || null;
});

const dmDisplayMember = computed(() => {
  if (dmPeer.value) return dmPeer.value;
  if (props.channel.name) return null;
  return (props.channel.members || [])[0] || null;
});

const isDeletedUserDM = computed(
  () => isDM.value && !dmPeer.value && !!props.channel.name
);

const channelName = computed(() => {
  if (isDM.value) {
    if (dmPeer.value) return dmPeer.value.name;
    return (
      props.channel.name ||
      (props.channel.members || [])[0]?.name ||
      'Direct Message'
    );
  }
  return props.channel.name || '';
});

const channelDescription = computed(() => props.channel.description || '');
const memberCount = computed(() => props.channel.members?.length || props.channel.members_count || 0);
const isArchived = computed(() => props.channel.status === 'archived');
const isMuted = computed(() => !!props.channel.muted);
const isFavorited = computed(() => !!props.channel.favorited);

const channelIcon = computed(() => {
  if (isDM.value) return 'i-lucide-message-circle';
  if (isPrivate.value) return 'i-lucide-lock';
  return 'i-lucide-hash';
});

const pinnedContent = computed(() => {
  if (!props.pinnedMessages.length) return '';
  const content = props.pinnedMessages[0].content || '';
  return content.length > 100 ? `${content.substring(0, 100)}...` : content;
});

const pinnedCountLabel = computed(() => {
  if (props.pinnedMessages.length <= 1) return '';
  return `(${props.pinnedMessages.length})`;
});

function toggleMenu() {
  showMenu.value = !showMenu.value;
}

function closeMenu() {
  showMenu.value = false;
}

function handleMenuAction(action) {
  closeMenu();
  emit(action);
}
</script>

<template>
  <div>
    <div
      class="flex h-[53px] items-center gap-3 border-b border-n-slate-5 bg-n-solid-2 px-4"
    >
      <!-- Left: back button (mobile) + favorite star + icon/avatar + name -->
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <!-- Back to sidebar (mobile only) -->
        <button
          v-if="showBackButton"
          class="flex-shrink-0 flex items-center justify-center rounded-lg p-1 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-label="t('INTERNAL_CHAT.CHANNEL.BACK_TO_CHANNELS')"
          @click="emit('back')"
        >
          <Icon icon="i-lucide-chevron-left" class="size-5" />
        </button>
        <!-- Favorite star: first icon of all -->
        <button
          class="flex-shrink-0 rounded p-0.5 transition-colors"
          :class="isFavorited ? 'text-n-amber-9' : 'text-n-slate-8 hover:text-n-amber-9'"
          :title="isFavorited ? t('INTERNAL_CHAT.CHANNEL.UNFAVORITE') : t('INTERNAL_CHAT.CHANNEL.FAVORITE')"
          :aria-label="isFavorited ? t('INTERNAL_CHAT.CHANNEL.UNFAVORITE') : t('INTERNAL_CHAT.CHANNEL.FAVORITE')"
          @click="emit('toggleFavorite')"
        >
          <Icon
            icon="i-lucide-star"
            class="size-4"
            :class="isFavorited ? 'fill-current' : ''"
          />
        </button>

        <Avatar
          v-if="isDM"
          :name="channelName"
          :src="dmDisplayMember?.avatar_url || ''"
          :status="dmDisplayMember?.availability_status"
          :size="28"
          rounded-full
          hide-offline-status
        />
        <Icon
          v-else
          :icon="channelIcon"
          class="size-5 text-n-slate-11 flex-shrink-0"
        />

        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <button
              class="truncate text-sm font-semibold text-n-slate-12 hover:underline cursor-pointer"
              type="button"
              @click="emit('info', { tab: 'about' })"
            >
              {{ channelName }}
            </button>
            <span
              v-if="isDeletedUserDM"
              class="flex-shrink-0 text-xs italic text-n-slate-9"
            >
              ({{ t('INTERNAL_CHAT.MESSAGE.DELETED_USER') }})
            </span>
            <span
              v-if="isArchived"
              class="flex-shrink-0 rounded bg-n-slate-4 px-1.5 py-0.5 text-xs text-n-slate-10"
            >
              {{ t('INTERNAL_CHAT.CHANNEL.ARCHIVED') }}
            </span>
          </div>
          <p v-if="channelDescription" class="truncate text-xs text-n-slate-10">
            {{ channelDescription }}
          </p>
        </div>
      </div>

      <!-- Right: member count + mute + search + ellipsis menu -->
      <div class="flex items-center gap-1 flex-shrink-0">
        <button
          v-if="memberCount > 0"
          type="button"
          class="flex items-center gap-1 text-xs text-n-slate-10 mr-1 rounded-lg px-1.5 py-1 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
          :title="t('INTERNAL_CHAT.CHANNEL.MEMBERS')"
          @click="emit('info', { tab: 'members' })"
        >
          <Icon icon="i-lucide-users" class="size-3.5" />
          {{ memberCount }}
        </button>

        <!-- Mute toggle -->
        <button
          class="flex items-center justify-center rounded-lg p-1.5 transition-colors"
          :class="isMuted
            ? 'text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12'
            : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'"
          :title="isMuted ? t('INTERNAL_CHAT.CHANNEL.UNMUTE') : t('INTERNAL_CHAT.CHANNEL.MUTE')"
          :aria-label="isMuted ? t('INTERNAL_CHAT.CHANNEL.UNMUTE') : t('INTERNAL_CHAT.CHANNEL.MUTE')"
          @click="emit('toggleMute')"
        >
          <Icon
            :icon="isMuted ? 'i-lucide-bell-off' : 'i-lucide-bell'"
            class="size-4"
          />
        </button>

        <!-- Search (no function yet) -->
        <button
          class="flex items-center justify-center rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          :title="t('INTERNAL_CHAT.SEARCH.TITLE')"
          :aria-label="t('INTERNAL_CHAT.SEARCH.TITLE')"
        >
          <Icon icon="i-lucide-search" class="size-4" />
        </button>

        <!-- Ellipsis dropdown -->
        <div class="relative">
          <button
            class="flex items-center justify-center rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            :class="{ 'bg-n-alpha-2 text-n-slate-12': showMenu }"
            :aria-label="t('INTERNAL_CHAT.CHANNEL.ACTIONS')"
            @click="toggleMenu"
          >
            <Icon icon="i-lucide-ellipsis-vertical" class="size-4" />
          </button>

          <!-- Backdrop to close -->
          <div
            v-if="showMenu"
            class="fixed inset-0 z-40"
            @click="closeMenu"
          />

          <!-- Dropdown menu -->
          <div
            v-if="showMenu"
            class="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-n-slate-5 bg-n-solid-2 py-1 shadow-lg"
          >
            <!-- Informações -->
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="closeMenu; emit('info', { tab: 'about' })"
            >
              <Icon icon="i-lucide-info" class="size-4 text-n-slate-11" />
              {{ t('INTERNAL_CHAT.CHANNEL.INFO') }}
            </button>

            <!-- Ver perfil completo (DM only) -->
            <button
              v-if="isDM"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="closeMenu; emit('info', { tab: 'about' })"
            >
              <Icon icon="i-lucide-user" class="size-4 text-n-slate-11" />
              {{ t('INTERNAL_CHAT.CHANNEL.VIEW_PROFILE') }}
            </button>

            <div class="my-1 h-px bg-n-slate-4" />

            <!-- Favorite toggle -->
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="handleMenuAction('toggleFavorite')"
            >
              <Icon
                :icon="isFavorited ? 'i-lucide-star-off' : 'i-lucide-star'"
                class="size-4 text-n-slate-11"
              />
              {{
                isFavorited
                  ? t('INTERNAL_CHAT.CHANNEL.UNFAVORITE')
                  : t('INTERNAL_CHAT.CHANNEL.FAVORITE')
              }}
            </button>

            <!-- Pesquisar -->
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="closeMenu"
            >
              <Icon icon="i-lucide-search" class="size-4 text-n-slate-11" />
              {{ isDM ? t('INTERNAL_CHAT.CHANNEL.SEARCH_IN_CONVERSATION') : t('INTERNAL_CHAT.CHANNEL.SEARCH_IN_CHANNEL') }}
            </button>

            <!-- Exibição dividida (no function yet) -->
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="closeMenu"
            >
              <Icon icon="i-lucide-columns-2" class="size-4 text-n-slate-11" />
              {{ t('INTERNAL_CHAT.CHANNEL.SPLIT_VIEW') }}
            </button>

            <div class="my-1 h-px bg-n-slate-4" />

            <!-- Configurações (non-DM) -->
            <button
              v-if="!isDM"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="closeMenu; emit('info', { tab: 'settings' })"
            >
              <Icon icon="i-lucide-settings" class="size-4 text-n-slate-11" />
              {{ t('INTERNAL_CHAT.CHANNEL.SETTINGS') }}
            </button>

            <!-- Fechar DM -->
            <button
              v-if="isDM"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="handleMenuAction('closeDm')"
            >
              <Icon icon="i-lucide-x-circle" class="size-4 text-n-slate-11" />
              {{ t('INTERNAL_CHAT.CHANNEL.CLOSE_DM') }}
            </button>

            <!-- Sair do canal (non-DM) -->
            <button
              v-if="!isDM"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-ruby-3"
              @click="handleMenuAction('leave')"
            >
              <Icon icon="i-lucide-log-out" class="size-4" />
              {{ t('INTERNAL_CHAT.CHANNEL.LEAVE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pinned message banner -->
    <button
      v-if="pinnedMessages.length > 0"
      type="button"
      class="flex w-full items-center gap-2 border-b border-n-slate-5 bg-n-amber-2 px-4 py-2 cursor-pointer hover:bg-n-amber-3 transition-colors"
      @click="emit('scrollToPinned', pinnedMessages[0])"
    >
      <Icon icon="i-lucide-pin" class="size-3.5 text-n-amber-11 flex-shrink-0" />
      <span class="text-xs font-medium text-n-amber-11">
        {{ t('INTERNAL_CHAT.PIN.PINNED_MESSAGE') }}
        <span v-if="pinnedCountLabel" class="ml-1">{{ pinnedCountLabel }}</span>
      </span>
      <span class="truncate text-xs text-n-slate-12">{{ pinnedContent }}</span>
    </button>

  </div>
</template>
